// Búsqueda + orden de los catálogos de productos de Interfaz V2. Lógica pura,
// compartida por los dos lugares donde se lista el catálogo completo:
// `PickProductPanel` (plantillas, modos de diseño, video-influencer, combinar
// productos) y `ProductFullMenu` (menú fullscreen del asistente). La UI del
// control vive en `components/ProductCatalogToolbar`.

import type { MockProductV2 } from './mockData';
import { buscarProductos } from '../../lib/busquedaDeProductos';

export type ProductCatalogSort = 'recent' | 'used';

export const PRODUCT_SORT_LABELS: Record<ProductCatalogSort, string> = {
  recent: 'Últimos subidos',
  used: 'Últimos utilizados',
};

export const DEFAULT_PRODUCT_SORT: ProductCatalogSort = 'recent';

// A partir de cuántos productos mostramos el toolbar. Con un catálogo chico
// entra todo en pantalla y un buscador es ruido; 8 = más de una fila y media
// en las grillas de 2 columnas.
export const MIN_PRODUCTS_FOR_TOOLBAR = 8;

/**
 * Filtra por nombre y, con `indice`, también por los datos del producto (análisis de IA,
 * descripción, características, categoría — ADR-2026-10-21-la-busqueda-mira-los-datos-del-producto).
 * Sin acentos ni mayúsculas; los que coinciden por nombre van primero. Query vacía = lista intacta.
 */
export function filterProductsByQuery<T extends { name: string; id?: string }>(
  products: T[],
  query: string,
  indice?: ReadonlyMap<string, string>,
): T[] {
  return buscarProductos(products, query, (p) => p.name, (p) => p.id, indice);
}

/**
 * Ordena según el criterio elegido.
 *
 *  • `recent` — la lista tal cual viene de `fetchUserProducts`, que ya la pide
 *    a la base por `created_at desc`. No re-ordenamos: varios call-sites
 *    (p.ej. `fetchUserProductsByBrand`) no completan `createdAt`, y ordenar por
 *    un campo ausente scramblearía el orden real.
 *  • `used` — por `lastUsedAt` desc. Los nunca usados van al fondo; como el
 *    sort de JS es estable, entre ellos (y entre empates) se conserva el orden
 *    de entrada, o sea "últimos subidos".
 */
export function sortProducts<T extends { lastUsedAt?: string | null }>(
  products: T[],
  sort: ProductCatalogSort,
): T[] {
  if (sort === 'recent') return products;
  const ts = (p: T): number => {
    const raw = p.lastUsedAt;
    if (!raw) return -Infinity;
    const n = Date.parse(raw);
    return Number.isNaN(n) ? -Infinity : n;
  };
  return [...products].sort((a, b) => ts(b) - ts(a));
}

export interface ProductCatalogFilterState {
  query: string;
  sort: ProductCatalogSort;
  /** Los datos de cada producto para buscar (`useIndiceDeBusqueda`). Sin él, sólo por nombre. */
  indice?: ReadonlyMap<string, string>;
}

export interface ProductCatalogFilterResult<T> {
  /** Lista final (filtrada + ordenada). */
  items: T[];
  /** Hay una búsqueda activa → la grilla se aplana (sin secciones). */
  isSearching: boolean;
  /** Buscó y no quedó nada → estado vacío de "sin resultados". */
  noResults: boolean;
}

export function applyProductCatalogFilter<
  T extends MockProductV2 | { name: string; id?: string; lastUsedAt?: string | null },
>(
  products: T[],
  { query, sort, indice }: ProductCatalogFilterState,
): ProductCatalogFilterResult<T> {
  const isSearching = query.trim().length > 0;
  // Ordenar ANTES de filtrar: el filtro pone primero los que coinciden por nombre y respeta,
  // dentro de cada grupo, el orden que recibe.
  const items = filterProductsByQuery(sortProducts(products, sort), query, indice);
  return { items, isSearching, noResults: isSearching && items.length === 0 };
}
